import { Fragment, useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Loader } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/vi/TourParts";
import { FileDown, ChevronDown, ChevronRight, AlertTriangle } from "lucide-react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import { money, downloadBlob } from "@/lib/vi";

const km = (v) => `${(v || 0).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} km`;
const opt = (v, f = money) => (v == null ? "—" : f(v));
const Ecart = ({ v }) => (v == null ? "—" : <span className={Math.abs(v) > 20 ? "text-orange-600 font-semibold" : ""}>{v > 0 ? "+" : ""}{v} %{Math.abs(v) > 20 && <AlertTriangle className="inline h-3 w-3 ml-1" />}</span>);
const HEAD = ["#", "Tricycle", "Tournées", "Km compteur", "Km GPS", "Écart", "Km retenus", "CA", "CA/km", "Carburant/km", "Visites/km"];

function Cells({ r }) {
  return <>
    <td className="px-2 py-1.5 text-right">{r.tournees}</td><td className="px-2 py-1.5 text-right font-mono">{km(r.km_compteur)}</td>
    <td className="px-2 py-1.5 text-right font-mono">{km(r.km_gps)}</td><td className="px-2 py-1.5 text-right"><Ecart v={r.ecart_pct} /></td>
    <td className="px-2 py-1.5 text-right font-mono font-semibold">{km(r.km)}</td><td className="px-2 py-1.5 text-right font-mono">{money(r.ca)}</td>
    <td className="px-2 py-1.5 text-right font-mono font-bold text-brand-700">{opt(r.ca_par_km)}</td><td className="px-2 py-1.5 text-right font-mono">{opt(r.carburant_par_km)}</td>
    <td className="px-2 py-1.5 text-right font-mono">{opt(r.visites_par_km, (x) => x)}</td>
  </>;
}

function Ranking({ d }) {
  const [open, setOpen] = useState("");
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="km-ranking">
        <thead><tr className="text-[11px] uppercase text-slate-500 border-b">{HEAD.map((h) => <th key={h} className="px-2 py-1.5 text-right first:text-left [&:nth-child(2)]:text-left">{h}</th>)}</tr></thead>
        <tbody>
          {d.rows.map((r, i) => (
            <Fragment key={r.id}>
              <tr className={`border-b cursor-pointer hover:bg-slate-50 ${i === 0 ? "bg-emerald-50" : i === d.rows.length - 1 && d.rows.length > 1 ? "bg-rose-50" : ""}`} onClick={() => setOpen(open === r.id ? "" : r.id)} data-testid={`km-row-${r.label}`}>
                <td className="px-2 py-1.5 font-bold">{i + 1}</td>
                <td className="px-2 py-1.5 font-mono font-semibold">{open === r.id ? <ChevronDown className="inline h-3.5 w-3.5" /> : <ChevronRight className="inline h-3.5 w-3.5" />} {r.label}</td>
                <Cells r={r} />
              </tr>
              {open === r.id && r.weeks.filter((w) => w.tournees).map((w) => (
                <tr key={w.start} className="border-b bg-slate-50/60 text-xs text-slate-600" data-testid={`km-week-${r.label}-${w.label}`}><td /><td className="px-2 py-1 pl-6">{w.label} · {w.start.slice(5)} → {w.end.slice(5)}</td><Cells r={w} /></tr>
              ))}
            </Fragment>
          ))}
          {d.rows.length > 0 && <tr className="font-bold border-t-2" data-testid="km-total"><td /><td className="px-2 py-1.5">TOTAL</td><Cells r={d.totaux} /></tr>}
        </tbody>
      </table>
      {!d.rows.length && <p className="text-sm text-slate-400 py-4">Aucune tournée sur la période.</p>}
    </div>
  );
}

export default function ViKmReport() {
  const [weeks, setWeeks] = useState(8);
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api.get(`/itinerant/km-report?weeks=${weeks}`).then((r) => setD(r.data)); }, [weeks]);
  return (
    <div data-testid="vi-km-report">
      <PageHeader title="Rapport kilomètres" subtitle="Kilomètres réels parcourus par tricycle, comparés au CA, semaine par semaine">
        <Button variant="outline" onClick={() => downloadBlob(`/itinerant/km-report/pdf?weeks=${weeks}`, "rapport-km.pdf")} data-testid="km-pdf"><FileDown className="h-4 w-4 mr-1.5" />PDF</Button>
      </PageHeader>
      <div className="flex gap-2 mb-4">
        {[4, 8, 12].map((w) => <Button key={w} size="sm" variant={weeks === w ? "default" : "outline"} className={weeks === w ? "bg-brand-600 text-white" : ""} onClick={() => setWeeks(w)} data-testid={`km-weeks-${w}`}>{w} semaines</Button>)}
      </div>
      {!d ? <Loader /> : <>
        <Card title={`Évolution hebdomadaire — ${d.start} → ${d.end}`} testid="km-chart">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={d.series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" fontSize={11} />
                <YAxis yAxisId="ca" fontSize={11} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                <YAxis yAxisId="km" orientation="right" fontSize={11} />
                <Tooltip formatter={(v, n) => (n === "Km" ? km(v) : money(v))} />
                <Legend />
                <Bar yAxisId="ca" dataKey="ca" name="CA" fill="#076633" radius={[4, 4, 0, 0]} />
                <Line yAxisId="km" dataKey="km" name="Km" stroke="#ea580c" strokeWidth={2} dot />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card title="Classement par rendement (CA par km)" testid="km-ranking-card">
          <Ranking d={d} />
          <p className="text-[11px] text-slate-400 mt-2">{d.note} Cliquez sur un tricycle pour voir le détail par semaine. Envoi automatique chaque lundi à 7 h aux responsables.</p>
        </Card>
      </>}
    </div>
  );
}
